import {useIsOverviewEnabled} from '../../hooks/use-feature-state'
import {Banner, Button, Link as PrimerLink} from '@primer/react'
import {CodeReviewIcon} from '@primer/octicons-react'
import {clsx} from 'clsx'

import type {ReviewRequestBanner as ReviewRequestBannerData} from '../../page-data/payloads/header'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {Link} from '@github-ui/react-router'
import styles from './ReviewRequestBanner.module.css'
import {useActiveRoute} from '../../hooks/use-active-route'
import {useIsRouteMounted} from '@github-ui/react-core/future/use-is-route-mounted'
import {pullRequestsConversationsRoute} from '../../routes/conversations-route'
import {useChildRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useLayoutQuery} from '../../page-data/loaders/use-layout-query'

export function ReviewRequestBanner() {
  const activeRoute = useActiveRoute()
  const isConversationRouteMounted = useIsRouteMounted(pullRequestsConversationsRoute)

  if (activeRoute !== 'conversation') return null

  return isConversationRouteMounted ? <ConversationReviewRequestBanner /> : <ReviewRequestBannerContent />
}

function ConversationReviewRequestBanner() {
  const {data} = useChildRouteQuery(pullRequestsConversationsRoute, 'mainQuery')

  return <ReviewRequestBannerContent reviewRequest={data?.reviewRequestBanner} />
}

function ReviewRequestBannerContent({reviewRequest}: {reviewRequest?: ReviewRequestBannerData}) {
  const {bannersData, urls} = useLayoutQuery().data
  const resolvedReviewRequest = reviewRequest ?? bannersData.banners.reviewRequestBanner
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags --
     TODO: Migrate to isFeatureEnabled from @github-ui/feature-flags. See
     https://thehub.github.com/epd/engineering/dev-practicals/frontend/react/data-router/migration/app-payload-deprecation/ */
  const isChangesEnabled = useFeatureFlag('react_data_router_pull_request_files')
  const url = isChangesEnabled ? `${urls.changes}` : `${urls.files}`
  const showUpdatedHeader = useIsOverviewEnabled()

  if (!resolvedReviewRequest?.render) return null

  const message =
    resolvedReviewRequest.requestedBySelf || !resolvedReviewRequest.requestingUserDisplayLogin ? (
      'This pull request is waiting on your review.'
    ) : (
      <>
        <PrimerLink href={`/${resolvedReviewRequest.requestingUserDisplayLogin}`} className="Link--primary text-bold">
          {resolvedReviewRequest.requestingUserDisplayLogin}
        </PrimerLink>{' '}
        requested your review on this pull request.
      </>
    )

  // leadingVisual is only supported for the info and upsell variants
  const variantAndVisualProps = showUpdatedHeader
    ? {variant: 'info' as const, leadingVisual: <CodeReviewIcon />}
    : {
        variant: 'warning' as const,
        primaryAction: (
          <Button as={Link} variant="primary" size="small" to={url} className="mr-1">
            Add your review
          </Button>
        ),
      }

  return (
    <Banner
      aria-label="Review request banner"
      className={clsx('flex-row width-full', showUpdatedHeader && styles.warningOverride)}
      title="Review requested"
      layout="compact"
      hideTitle
      {...variantAndVisualProps}
    >
      <Banner.Description>{message}</Banner.Description>
    </Banner>
  )
}
